import { FILL_MS, loaderProgress } from "./loader";

const KEY = "otridon:switch";
const root = document.documentElement;

interface Saved {
  at: number;
  section: string;
  into: number;
  from: { name: string; lang: string };
  to: { name: string; lang: string };
  level: number;
}

const sections = () =>
  Array.from(document.querySelectorAll<HTMLElement>("main section[id], main > div[id], footer[id]"));
const q = <T extends HTMLElement>(sel: string) => document.querySelector<T>(sel);
const PACE = 100 / FILL_MS;

function show(level: number): void {
  const count = q("[data-switch-count]");
  if (count) count.textContent = String(Math.round(level));
}

function label(saved: Pick<Saved, "from" | "to">): void {
  const to = q("[data-switch-to]"), from = q("[data-switch-from]");
  if (to) {
    to.textContent = saved.to.name;
    to.lang = saved.to.lang;
  }
  if (from) {
    from.textContent = saved.from.name;
    from.lang = saved.from.lang;
  }
}

export function switchTo(href: string, to: { name: string; lang: string }, from: { name: string; lang: string }): void {
  const line = window.innerHeight * 0.35;
  const here = sections().find((s) => {
    const r = s.getBoundingClientRect();
    return r.top <= line && r.bottom > line;
  });
  const saved: Saved = {
    at: Date.now(),
    section: here?.id ?? "",
    into: here ? (line - here.getBoundingClientRect().top) / Math.max(1, here.offsetHeight) : 0,
    from,
    to,
    level: 0,
  };
  label(saved);
  window.dispatchEvent(new Event("otridon:lock"));
  root.classList.add("is-switching");
  const started = performance.now();
  let leaving = false;
  show(0);
  const tick = (now: number) => {
    if (leaving) return;
    saved.level = Math.min(100, Math.max(0, (now - started) * PACE));
    show(saved.level);
    if (root.classList.contains("is-switching")) requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
  window.setTimeout(() => {
    leaving = true;
    show(saved.level);
    try {
      sessionStorage.setItem(KEY, JSON.stringify(saved));
    } catch {
    }
    location.assign(href);
  }, 700);
}

export function initSwitch(): Promise<void> {
  window.addEventListener("pageshow", (e) => {
    if (e.persisted) {
      root.classList.remove("is-switching");
      window.dispatchEvent(new Event("otridon:unlock"));
    }
  });
  if (!root.classList.contains("is-arriving")) return Promise.resolve();
  let saved: Saved | null = null;
  try {
    saved = JSON.parse(sessionStorage.getItem(KEY) ?? "null") as Saved | null;
    sessionStorage.removeItem(KEY);
  } catch {
    saved = null;
  }
  if (saved?.from && saved.to) label(saved);
  const from = typeof saved?.level === "number" ? saved.level : 0;
  show(from);

  return new Promise<void>((resolve) => {
    const started = performance.now();
    let level = from;
    let last = started;
    const part = () => {
      const target = saved?.section ? document.getElementById(saved.section) : null;
      if (target) {
        const top = target.getBoundingClientRect().top + window.scrollY;
        window.scrollTo(0, Math.max(0, top + (saved?.into ?? 0) * target.offsetHeight - window.innerHeight * 0.35));
      }
      root.classList.add("is-parting");
      window.setTimeout(() => {
        root.classList.add("is-opening");
        resolve();
        window.setTimeout(() => root.classList.remove("is-arriving", "is-parting", "is-opening"), 900);
      }, 1350);
    };
    const tick = (now: number) => {
      const dt = Math.min(0.1, (now - last) / 1000);
      last = now;
      const ready = now - started > 6000 ? 100 : Math.max(level, loaderProgress());
      level = Math.min(ready, level + dt * 1000 * PACE);
      show(level);
      if (Math.round(level) >= 100) {
        window.setTimeout(part, 180);
        return;
      }
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  });
}
